import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import {
  PlusIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { Task, TaskStatus } from '../../../types/task.types';
import { TaskCardMini } from '../components';
import { Badge, Skeleton } from '../../ui';
import taskService from '../../../services/taskService';

interface TaskBoardViewProps {
  tasks: Task[];
  loading: boolean;
  onTaskClick: (task: Task) => void;
  onTaskComplete: (taskId: number) => void;
  onRefresh: () => void;
}

interface Column {
  id: TaskStatus;
  title: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const columns: Column[] = [
  {
    id: TaskStatus.NOT_STARTED,
    title: 'To Do',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
  {
    id: TaskStatus.IN_PROGRESS,
    title: 'In Progress',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  {
    id: TaskStatus.COMPLETED,
    title: 'Completed',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
  {
    id: TaskStatus.DEFERRED,
    title: 'Deferred',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
];

const TaskBoardView: React.FC<TaskBoardViewProps> = ({
  tasks,
  loading,
  onTaskClick,
  onTaskComplete,
  onRefresh,
}) => {
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | null>(null);

  const columnTasks = useMemo(() => {
    const result: Record<TaskStatus, Task[]> = {
      [TaskStatus.NOT_STARTED]: [],
      [TaskStatus.TODO]: [],
      [TaskStatus.IN_PROGRESS]: [],
      [TaskStatus.COMPLETED]: [],
      [TaskStatus.CANCELLED]: [],
      [TaskStatus.DEFERRED]: [],
      [TaskStatus.OVERDUE]: [],
    };

    tasks.forEach(task => {
      if (result[task.status]) {
        result[task.status].push(task);
      }
    });

    // Sort by priority and due date
    Object.keys(result).forEach(status => {
      result[status as TaskStatus].sort((a, b) => {
        const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, NORMAL: 3, LOW: 4 };
        const aPriority = priorityOrder[a.priority] || 3;
        const bPriority = priorityOrder[b.priority] || 3;
        if (aPriority !== bPriority) return aPriority - bPriority;
        if (a.dueDateTime && b.dueDateTime) {
          return new Date(a.dueDateTime).getTime() - new Date(b.dueDateTime).getTime();
        }
        return 0;
      });
    });

    return result;
  }, [tasks]);

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id.toString());
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: TaskStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: TaskStatus) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedTask || draggedTask.status === targetStatus) {
      setDraggedTask(null);
      return;
    }

    try {
      await taskService.updateTask(draggedTask.id, { status: targetStatus });
      onRefresh();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
    setDraggedTask(null);
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="h-[calc(100vh-350px)] min-h-[500px]">
      <div className="flex gap-4 h-full overflow-x-auto pb-4">
        {columns.map(column => {
          const tasks = columnTasks[column.id] || [];
          const isDropTarget = dragOverColumn === column.id;

          return (
            <div
              key={column.id}
              className={clsx(
                'flex-shrink-0 w-80 rounded-2xl flex flex-col transition-all duration-200',
                column.bgColor,
                isDropTarget && 'ring-2 ring-primary-400 ring-offset-2'
              )}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <div className={clsx('p-4 border-b', column.borderColor)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className={clsx('font-semibold', column.color)}>{column.title}</h3>
                    <Badge size="sm" variant="neutral">{tasks.length}</Badge>
                  </div>
                </div>
              </div>

              {/* Column Content */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {tasks.map(task => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    onDragEnd={handleDragEnd}
                    className={clsx(
                      'transition-transform duration-200',
                      draggedTask?.id === task.id && 'opacity-50'
                    )}
                  >
                    <TaskCardMini
                      task={task}
                      onClick={() => onTaskClick(task)}
                      onComplete={() => onTaskComplete(task.id)}
                      isDragging={draggedTask?.id === task.id}
                    />
                  </div>
                ))}

                {/* Empty state */}
                {tasks.length === 0 && (
                  <div className="h-full flex items-center justify-center p-8">
                    <div className="text-center text-gray-400">
                      <div className="h-12 w-12 mx-auto mb-2 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <PlusIcon className="h-6 w-6" />
                      </div>
                      <p className="text-sm">Drop tasks here</p>
                    </div>
                  </div>
                )}

                {/* Drop zone indicator */}
                {isDropTarget && tasks.length > 0 && (
                  <div className="h-24 rounded-lg border-2 border-dashed border-primary-400 bg-primary-50 flex items-center justify-center">
                    <span className="text-primary-600 text-sm font-medium">Drop here</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="flex gap-4 h-[500px]">
    {[1, 2, 3, 4].map(i => (
      <Skeleton key={i} className="flex-shrink-0 w-80 h-full rounded-2xl" />
    ))}
  </div>
);

export default TaskBoardView;

